import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, ChartNoAxesCombined, FileText } from "lucide-react";
import SalesDashboard from "../Sales/SalesDashboard";
import LeadsDashboard from "./LeadsDashboard";
import DashboardContent from "./DashboardContent";
import DemandLetterApproval from "./DemandLetterApproval";
import usePermission from "../../hooks/usePermission";
import { PERMISSIONS } from "../../constants/permissions";

/**
 * Sales screen shell — three sibling tabs (Dashboard / Leads / Customers)
 * for full-access users, two (Dashboard / Leads) for presales-only users.
 * Tab state is mirrored in the URL hash so deep links and refreshes land
 * on the same tab. Renders the active child below a pill-style tab strip
 * matching the rest of the app's nav family.
 */
function CustomerDashboard() {
    const [activeTab, setActiveTab] = useState("dashboard");
    const location = useLocation();
    const navigate = useNavigate();
    const { can, isDeveloperAdmin } = usePermission();

    // Presales-style users (Manage Presales bundle): show Leads only, no Customers tab.
    // Anyone with sale.read or customer.read sees the full set.
    const isPresale =
        !isDeveloperAdmin() &&
        can(PERMISSIONS.LEAD_CREATE) &&
        !can(PERMISSIONS.CUSTOMER_READ) &&
        !can(PERMISSIONS.SALE_READ);

    const tabData = useMemo(
        () =>
            isPresale
                ? [
                      { label: "Dashboard", value: "dashboard", icon: LayoutDashboard },
                      { label: "Leads", value: "leads", icon: Users },
                  ]
                : [
                      { label: "Dashboard", value: "dashboard", icon: LayoutDashboard },
                      { label: "Leads", value: "leads", icon: Users },
                      { label: "Customers", value: "customers", icon: ChartNoAxesCombined },
                  ],
        [isPresale]
    );

    useEffect(() => {
        const hash = location.hash.slice(1);
        if (tabData.find((tab) => tab.value === hash)) {
            setActiveTab(hash);
        }
    }, [location.hash, tabData]);

    const handleTabChange = (tabValue) => {
        setActiveTab(tabValue);
        navigate(`#${tabValue}`);
    };

    const renderActiveTab = () => {
        switch (activeTab) {
            case "dashboard":
                return <DashboardContent />;
            case "leads":
                return <LeadsDashboard />;
            case "customers":
                return <SalesDashboard />;
            case "actions":
                return <DemandLetterApproval />;
            default:
                return <DashboardContent />;
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)] bg-slate-50/40">
            {/* Pill tab nav — matches dashboard / reports / approvals. */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5 inline-flex gap-1 self-start flex-wrap">
                {tabData.map(({ value, label, icon: Icon }) => (
                    <button
                        key={value}
                        type="button"
                        onClick={() => handleTabChange(value)}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                            activeTab === value
                                ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                        }`}
                    >
                        {Icon && <Icon size={14} />}
                        {label}
                    </button>
                ))}
            </div>

            {/* Active tab body — each child renders its own hero + content. */}
            <div className="flex-1 min-w-0">{renderActiveTab()}</div>
        </div>
    );
}

export default CustomerDashboard;
